import { useEffect, type KeyboardEvent } from "react";
import { useForm } from "@tanstack/react-form";

import {
  Button,
  DatePicker,
  DatePickerFieldTrigger,
  ensureV2DatepickerPortal,
  RadioGroup,
  Select,
  TextField,
} from "../../../ui/index.ts";
import {
  BLOOD_GROUP_VALUES,
  MARITAL_STATUS_VALUES,
  personalStepSchema,
  type PersonalStepValues,
} from "../schemas/create-employee.schema.ts";
import { formatFormFieldErrors } from "../../auth/forms/form-errors.ts";

import styles from "./personal-step.module.css";
import { formatName } from "../../../../utils/format-name.ts";

export type PersonalStepProps = {
  defaultValues: PersonalStepValues;
  loading: boolean;
  onNext: (v: PersonalStepValues) => void;
  formId?: string;
  hideFooter?: boolean;
  onResetReady?: (reset: () => void) => void;
};

const field = (name: keyof PersonalStepValues) => name;

const GENDER_OPTIONS = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
  { value: "others", label: "Others" },
];

const YES_NO_OPTIONS = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
];

function localIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

/** Parse `yyyy-mm-dd` as a local calendar date (avoids UTC off-by-one). */
function parseIsoToDate(iso: string): Date | null {
  if (iso == null || iso.trim() === "") return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]) - 1;
  const d = Number(m[3]);
  const dt = new Date(y, mo, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== mo || dt.getDate() !== d) return null;
  return dt;
}

function digitsOnly(value: string, maxLen: number): string {
  return value.replace(/\D/g, "").slice(0, maxLen);
}

function nameCharactersOnly(value: string): string {
  return value.replace(/[^a-zA-Z ]/g, "");
}

function insertNameSpace(
  event: KeyboardEvent<HTMLInputElement>,
  value: string,
  onChange: (value: string) => void,
): void {
  if (event.key !== " ") return;
  event.preventDefault();
  const start = event.currentTarget.selectionStart ?? value.length;
  const end = event.currentTarget.selectionEnd ?? start;
  onChange(`${value.slice(0, start)} ${value.slice(end)}`);
  const input = event.currentTarget;
  window.setTimeout(() => input.setSelectionRange(start + 1, start + 1), 0);
}

function blankPersonal(personalEmail = ""): PersonalStepValues {
  return {
    firstName: "",
    middleName: "",
    lastName: "",
    personalEmail,
    gender: "",
    bloodGroup: "",
    dob: "",
    aadharNumber: "",
    panCardNumber: "",
    passportNumber: "",
    joiningDate: "",
    maritalStatus: "",
    anniversaryDate: "",
    spouseName: "",
    spouseDob: "",
    hasChild: undefined,
    childName: "",
    childDob: "",
    childGender: undefined,
  } as PersonalStepValues;
}

export function PersonalStep({
  defaultValues,
  loading,
  onNext,
  formId,
  hideFooter = false,
  onResetReady,
}: PersonalStepProps) {
  const today = new Date();
  const todayStart = startOfDay(today);
  const minJoinCal = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 30);
  const joiningMinStart = startOfDay(
    new Date(minJoinCal.getFullYear(), minJoinCal.getMonth(), minJoinCal.getDate()),
  );
  const childDobMin = new Date(
    today.getFullYear() - 18,
    today.getMonth(),
    today.getDate() + 1,
  );



  useEffect(() => {
    ensureV2DatepickerPortal();
  }, []);

  const form = useForm({
    defaultValues,
    // Zod string refinements widen inferred input vs. strict output unions on defaultValues.
    validators: { onSubmit: personalStepSchema as never },
    onSubmit: async ({ value }) => {
      await onNext(value as PersonalStepValues);
    },
  });

  useEffect(() => {
    onResetReady?.(() => {
      const email = form.state.values.personalEmail;
      form.reset(blankPersonal(email));
    });
  }, [form, onResetReady]);

  return (
    <form
      id={formId}
      className={styles.form}
      onSubmit={(e) => {
        e.preventDefault();
        void form.handleSubmit();
      }}
      noValidate
    >
      <div className={styles.grid}>
        <form.Field name={field("firstName")}>
          {(f) => (
            <TextField
              id="create-employee-first-name"
              label="First Name"
              value={f.state.value}
              maxLength={100}
              onChange={(e) => f.handleChange(formatName(e.target.value))}
              onBlur={f.handleBlur}
              error={formatFormFieldErrors(f.state.meta.errors)}
              disabled={loading}
              required
            />
          )}
        </form.Field>
        <form.Field name={field("middleName")}>
          {(f) => (
            <TextField
              id="create-employee-middle-name"
              label="Middle Name"
              value={f.state.value ?? ""}
              maxLength={100}
              onChange={(e) => f.handleChange(formatName(e.target.value))} onBlur={f.handleBlur}
              error={formatFormFieldErrors(f.state.meta.errors)}
              disabled={loading}
            />
          )}
        </form.Field>
        <form.Field name={field("lastName")}>
          {(f) => (
            <TextField
              id="create-employee-last-name"
              label="Last Name"
              value={f.state.value}
              maxLength={100}
              onChange={(e) => f.handleChange(formatName(e.target.value))} onBlur={f.handleBlur}
              error={formatFormFieldErrors(f.state.meta.errors)}
              disabled={loading}
              required
            />
          )}
        </form.Field>
        <div className={styles.emailGenderRow}>
          <div className={styles.emailGenderCell}>
            <form.Field name={field("personalEmail")}>
              {(f) => (
                <TextField
                  id="create-employee-personal-email"
                  label="Personal Email Address"
                  type="email"
                  autoComplete="email"
                  value={f.state.value}
                  onChange={(e) => f.handleChange(e.target.value)}
                  onBlur={f.handleBlur}
                  error={formatFormFieldErrors(f.state.meta.errors)}
                  disabled
                  readOnly
                  required
                />
              )}
            </form.Field>
          </div>
          <div className={styles.emailGenderCell}>
            <form.Field name={field("gender")}>
              {(f) => (
                <RadioGroup
                  id="create-employee-gender"
                  name="create-employee-gender"
                  label="Gender"
                  required
                  options={GENDER_OPTIONS}
                  value={f.state.value ?? ""}
                  onChange={(v) => f.handleChange(v)}
                  onBlur={f.handleBlur}
                  error={formatFormFieldErrors(f.state.meta.errors)}
                  disabled={loading}
                />
              )}
            </form.Field>
          </div>
        </div>
        <form.Field name={field("bloodGroup")}>
          {(f) => (
            <Select
              id="create-employee-blood-group"
              label="Blood Group"
              required
              value={f.state.value ?? ""}
              onChange={(v) => {
                f.handleChange(v);
                if (v !== "Married") {
                  form.setFieldValue("spouseName", "");
                  form.setFieldValue("spouseDob", "");
                  form.setFieldValue("anniversaryDate", "");
                  form.setFieldValue("hasChild", undefined);
                  form.setFieldValue("childName", "");
                  form.setFieldValue("childDob", "");
                  form.setFieldValue("childGender", undefined);
                }
              }}
              options={BLOOD_GROUP_VALUES.map((bg) => ({
                value: bg,
                label: bg,
              }))}
              placeholder="Select Blood Group"
              disabled={loading}
              error={formatFormFieldErrors(f.state.meta.errors)}
            />
          )}
        </form.Field>
        <form.Field name={field("dob")}>
          {(f) => (
            <DatePicker
              id="create-employee-dob"
              label="Date of Birth"
              required
              disabled={loading}
              dateFormat="dd-MM-yyyy"
              placeholderText="dd-mm-yyyy"
              maxDate={todayStart}
              selected={parseIsoToDate(f.state.value ?? "")}
              onChange={(date) => f.handleChange(localIsoDate(date))}
              customInput={<DatePickerFieldTrigger />}
              error={formatFormFieldErrors(f.state.meta.errors)}
            />
          )}
        </form.Field>
        <form.Field name={field("aadharNumber")}>
          {(f) => (
            <TextField
              id="create-employee-aadhar"
              label="Aadhar Number"
              inputMode="numeric"
              autoComplete="off"
              maxLength={12}
              value={f.state.value}
              onChange={(e) => f.handleChange(digitsOnly(e.target.value, 12))}
              onBlur={f.handleBlur}
              error={formatFormFieldErrors(f.state.meta.errors)}
              disabled={loading}
              required
            />
          )}
        </form.Field>
        <form.Field name={field("panCardNumber")}>
          {(f) => (
            <TextField
              id="create-employee-pan"
              label="PAN Card Number"
              autoComplete="off"
              maxLength={10}
              value={f.state.value}
              onChange={(e) => f.handleChange(e.target.value.toUpperCase())}
              onBlur={f.handleBlur}
              error={formatFormFieldErrors(f.state.meta.errors)}
              disabled={loading}
              required
            />
          )}
        </form.Field>
        <form.Field name={field("passportNumber")}>
          {(f) => (
            <TextField
              id="create-employee-passport"
              label="Passport Number (If Applicable)"
              autoComplete="off"
              maxLength={12}
              value={f.state.value}
              onChange={(e) => f.handleChange(e.target.value.slice(0, 12))}
              onBlur={f.handleBlur}
              error={formatFormFieldErrors(f.state.meta.errors)}
              disabled={loading}
            />
          )}
        </form.Field>
        <form.Field name={field("joiningDate")}>
          {(f) => (
            <DatePicker
              id="create-employee-joining-date"
              label="Date of Joining"
              required
              disabled={loading}
              dateFormat="dd-MM-yyyy"
              placeholderText="dd-mm-yyyy"
              minDate={joiningMinStart}
              maxDate={undefined}
              selected={parseIsoToDate(f.state.value ?? "")}
              onChange={(date) => f.handleChange(localIsoDate(date))}
              customInput={<DatePickerFieldTrigger />}
              error={formatFormFieldErrors(f.state.meta.errors)}
            />
          )}
        </form.Field>
        <form.Field name={field("maritalStatus")}>
          {(f) => (
            <Select
              id="create-employee-marital-status"
              label="Marital Status"
              required
              value={f.state.value ?? ""}
              onChange={(v) => f.handleChange(v)}
              options={MARITAL_STATUS_VALUES.map((s) => ({
                value: s,
                label: s,
              }))}
              placeholder="Select Marital Status"
              disabled={loading}
              error={formatFormFieldErrors(f.state.meta.errors)}
            />
          )}
        </form.Field>
        <form.Subscribe selector={(s) => s.values.maritalStatus}>
          {(maritalStatus) =>
            maritalStatus === "Married" ? (
              <>
                <div className={styles.insuranceNote} role="note">
                  <span className={styles.noteIcon} aria-hidden>i</span>
                  <span>
                    The following information is required for enrollment under the
                    company&apos;s insurance policy. Please provide accurate details of your
                    dependents, if applicable.
                  </span>
                </div>
                <form.Field name={field("spouseName")}>
                  {(f) => (
                    <TextField
                      id="create-employee-spouse-name"
                      label="Spouse Name"
                      required
                      maxLength={100}
                      value={f.state.value ?? ""}
                      onChange={(e) =>
                        f.handleChange(
                          formatName(nameCharactersOnly(e.target.value)),
                        )
                      }
                      onKeyDown={(e) =>
                        insertNameSpace(e, f.state.value ?? "", f.handleChange)
                      }
                      onBlur={f.handleBlur}
                      disabled={loading}
                      error={formatFormFieldErrors(f.state.meta.errors)}
                    />
                  )}
                </form.Field>
                <form.Field name={field("spouseDob")}>
                  {(f) => (
                    <DatePicker
                      id="create-employee-spouse-dob"
                      label="Spouse Date of Birth"
                      required
                      disabled={loading}
                      dateFormat="dd-MM-yyyy"
                      placeholderText="dd-mm-yyyy"
                      maxDate={todayStart}
                      selected={parseIsoToDate(f.state.value ?? "")}
                      onChange={(date) => f.handleChange(localIsoDate(date))}
                      customInput={<DatePickerFieldTrigger />}
                      error={formatFormFieldErrors(f.state.meta.errors)}
                    />
                  )}
                </form.Field>
                <form.Field name={field("anniversaryDate")}>
                  {(f) => (
                    <DatePicker
                      id="create-employee-anniversary"
                      label="Anniversary Date"
                      required
                      disabled={loading}
                      dateFormat="dd-MM-yyyy"
                      placeholderText="dd-mm-yyyy"
                      maxDate={todayStart}
                      selected={parseIsoToDate(f.state.value ?? "")}
                      onChange={(date) => f.handleChange(localIsoDate(date))}
                      customInput={<DatePickerFieldTrigger />}
                      error={formatFormFieldErrors(f.state.meta.errors)}
                    />
                  )}
                </form.Field>
                <div className={styles.hasChildRow}>
                  <form.Field name={field("hasChild")}>
                    {(f) => (
                      <RadioGroup
                        id="create-employee-has-child"
                        name="create-employee-has-child"
                        label="Do you have a child below 18 years of age?"
                        required
                        options={YES_NO_OPTIONS}
                        value={f.state.value ?? ""}
                        onChange={(v) => {
                          f.handleChange(v as "yes" | "no");
                          if (v !== "yes") {
                            form.setFieldValue("childName", "");
                            form.setFieldValue("childDob", "");
                            form.setFieldValue("childGender", undefined);
                          }
                        }}
                        onBlur={f.handleBlur}
                        error={formatFormFieldErrors(f.state.meta.errors)}
                        disabled={loading}
                      />
                    )}
                  </form.Field>
                </div>
                <form.Subscribe selector={(s) => s.values.hasChild}>
                  {(hasChild) =>
                    hasChild === "yes" ? (
                      <section className={styles.childSection} aria-labelledby="child-details-title">                  
                        <div className={styles.childGrid}>
                          <form.Field name={field("childName")}>
                            {(f) => (
                              <TextField
                                id="create-employee-child-name"
                                label="Child Name"
                                required
                                maxLength={100}
                                value={f.state.value ?? ""}
                                onChange={(e) =>
                                  f.handleChange(
                                    formatName(nameCharactersOnly(e.target.value)),
                                  )
                                }
                                onKeyDown={(e) =>
                                  insertNameSpace(e, f.state.value ?? "", f.handleChange)
                                }
                                onBlur={f.handleBlur}
                                disabled={loading}
                                error={formatFormFieldErrors(f.state.meta.errors)}
                              />
                            )}
                          </form.Field>
                          <form.Field name={field("childDob")}>
                            {(f) => (
                              <DatePicker
                                id="create-employee-child-dob"
                                label="Date of Birth"
                                required
                                disabled={loading}
                                dateFormat="dd-MM-yyyy"
                                placeholderText="dd-mm-yyyy"
                                minDate={childDobMin}
                                maxDate={todayStart}
                                selected={parseIsoToDate(f.state.value ?? "")}
                                onChange={(date) => f.handleChange(localIsoDate(date))}
                                customInput={<DatePickerFieldTrigger />}
                                error={formatFormFieldErrors(f.state.meta.errors)}
                              />
                            )}
                          </form.Field>
                          <form.Field name={field("childGender")}>
                            {(f) => (
                              <RadioGroup
                                id="create-employee-child-gender"
                                name="create-employee-child-gender"
                                label="Gender"
                                required
                                options={GENDER_OPTIONS}
                                value={f.state.value ?? ""}
                                onChange={(v) =>
                                  f.handleChange(v as "male" | "female" | "others")
                                }
                                onBlur={f.handleBlur}
                                error={formatFormFieldErrors(f.state.meta.errors)}
                                disabled={loading}
                              />
                            )}
                          </form.Field>
                        </div>
                      </section>
                    ) : null
                  }
                </form.Subscribe>
              </>
            ) : null
          }
        </form.Subscribe>
      </div>
      {hideFooter ? null : (
        <div className={styles.actions}>
          <Button type="submit" disabled={loading}>
            {loading ? "Saving…" : "Next"}
          </Button>
        </div>
      )}
    </form>
  );
}
